import { ButtonBase, Stack, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { useRecoilValue } from 'recoil';
import ObliquePlaneMarker from '~/components/Annotations/ObliquePlaneMarker';
import AnnotationRowActions, {
    AnnotationRowLabelIds,
} from '~/components/Annotations/AnnotationRowActions';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import { AnnotationListState } from '~/state/atoms/annotationListAtoms';

interface IntensityAnnotationRowProps<T extends AnnotationRecord> {
    /** The list the row's annotation is in; the row reads its own record. */
    list: AnnotationListState<T>;
    id: string;
    icon: ReactElement;
    /** The main value shown for the annotation. */
    label: (item: T) => string;
    /** Whether the annotation's plane is not one of its image's own slices. */
    isOblique: (item: T) => boolean;
    selected: boolean;
    onActivate: () => void;
    onToggle: () => void;
    onDelete: () => void;
    onHover: (hovered: boolean) => void;
    labelIds: AnnotationRowLabelIds;
}

/**
 * A row in the intensity list, used for lines and circles alike: an icon,
 * the main value, and show/hide and delete buttons. Clicking the row selects
 * the annotation, shows its detail below and moves the viewport to it. The
 * row reads its own record by id.
 */
export default function IntensityAnnotationRow<T extends AnnotationRecord>({
    list,
    id,
    icon,
    label,
    isOblique,
    selected,
    onActivate,
    onToggle,
    onDelete,
    onHover,
    labelIds,
}: IntensityAnnotationRowProps<T>) {
    const item = useRecoilValue(list.item(id));
    if (item === undefined) {
        return null;
    }
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ opacity: item.visible ? 1 : 0.5 }}
            onMouseEnter={() => onHover(true)}
            onMouseLeave={() => onHover(false)}
        >
            <ButtonBase
                onClick={onActivate}
                // Selection is a state, not a colour: without this a reader
                // cannot tell which row is showing.
                aria-pressed={selected}
                sx={{
                    flexGrow: 1,
                    justifyContent: 'flex-start',
                    gap: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    bgcolor: selected ? 'action.selected' : undefined,
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                {icon}
                <Typography
                    variant="body2"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                    {label(item)}
                </Typography>
                {isOblique(item) && (
                    <ObliquePlaneMarker labelId="intensity.obliqueRow" />
                )}
            </ButtonBase>
            <AnnotationRowActions
                visible={item.visible}
                onToggleVisibility={onToggle}
                onDelete={onDelete}
                labelIds={labelIds}
            />
        </Stack>
    );
}
